import { createApi } from "@reduxjs/toolkit/query/react"
import { getUpdatedStates } from "utils/localCacheStateUtils"
import { CREATE_LIST, DELETE_LIST, EDIT_LIST } from "constants/index"
import { baseQueryWithMiddlewares } from "appRedux/baseQuery"
import { RootState } from "appRedux/store"
import { updateUserProfile, updateUserWishLists, updateUserWishListsWithFirstFourItems } from "appRedux/slices/userSlice"
import { userProfileApi } from "appRedux/slices/userApiSlice"
import { updateCurrentItemIndex, updateCurrentItemInfo, updateWishListInfo } from "appRedux/slices/wishListSlice"

export const wishListApi = createApi({
  reducerPath: "wishListApi",
  baseQuery: baseQueryWithMiddlewares("/wishlist"),
  endpoints: (builder) => ({
    getWishLists: builder.query<GetWishListsResponseDto, void>({
      query: () => "",
      keepUnusedDataFor: 300,
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateUserWishLists(data))
        } catch (error) {
          console.error(`Error getting wishlists: `, error)
        }
      }
    }),
    getWishList: builder.query<WishList, number>({
      query: (id) => `/${id}`,
      keepUnusedDataFor: 5,
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateWishListInfo(data))
        } catch (error) {
          dispatch(updateWishListInfo(null))
          dispatch(updateCurrentItemInfo(null))
          dispatch(updateCurrentItemIndex(-1))
          console.error(`Error getting wishlist with id: ${id}: `, error)
        }
      }
    }),
    createWishList: builder.mutation({
      query: (body) => ({
        url: "",
        method: "PUT",
        body: body
      }),
      transformResponse: (response: number) => ({ id: response }),
      onQueryStarted: async (body, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          const state = getState() as RootState
          const userProfile = state.user.profile
          const wishlistsWithFirstFourItems = state.user.wishListsWithFirstFourItems || []
          const showCreateNewListOverlay = state.itemForm.showCreateNewListOverlay
          if (!userProfile) return
          const { listInfo, userProfileLists, userWishLists } = getUpdatedStates(
            CREATE_LIST,
            wishlistsWithFirstFourItems,
            { ...body, id: data.id },
            null
          )

          if (!showCreateNewListOverlay) {
            dispatch(updateWishListInfo(listInfo))
          } else {
            document.dispatchEvent(new CustomEvent("select-list", { detail: { id: data.id } }))
          }
          dispatch(updateUserProfile({ ...userProfile, wishlistCount: userProfile?.wishlistCount + 1 }))
          dispatch(updateUserWishListsWithFirstFourItems({ wishlists: userProfileLists }))
          dispatch(updateUserWishLists({ wishlists: userWishLists }))

          dispatch(userProfileApi.util.updateQueryData("getUserProfile", undefined, (prevData) => {
            prevData.wishlistCount += 1
          }))
          if (state.user.profile?.handle) {
            dispatch(userProfileApi.util.updateQueryData("getWishListsWithFirstFourItems", state.user.profile?.handle, (prevData) => {
              prevData.wishlists = userProfileLists
            }))
          }
          dispatch(wishListApi.util.updateQueryData("getWishLists", undefined, (prevData) => {
            if (!prevData.wishlists || !listInfo) return
            prevData.wishlists.push(listInfo)
          }))
        } catch (error) {
          console.error("Error adding list:", error)
        }
      }
    }),
    editWishList: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/${id}`,
        method: "PATCH",
        body: body
      }),
      onQueryStarted: async ({ id, ...body }, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled
          const state = getState() as RootState
          const userProfile = state.user.profile
          const wishlistsWithFirstFourItems = state.user.wishListsWithFirstFourItems || []
          if (!userProfile) return
          const { userProfileLists, userWishLists } = getUpdatedStates(
            EDIT_LIST,
            wishlistsWithFirstFourItems,
            { ...body, id: id },
            null
          )

          dispatch(updateUserWishLists({ wishlists: userWishLists }))
          dispatch(updateUserWishListsWithFirstFourItems({ wishlists: userProfileLists }))

          dispatch(wishListApi.util.updateQueryData("getWishLists", undefined, (prevData) => {
            if (!prevData.wishlists) return
            prevData.wishlists = prevData.wishlists.map(list => {
              if (Number(list.id) === Number(id)) {
                return { ...list, name: body.name, privacy: body.privacy }
              } else {
                return list
              }
            })
          }))
          if (state.user.profile?.handle) {
            dispatch(userProfileApi.util.updateQueryData("getWishListsWithFirstFourItems", state.user.profile?.handle, (prevData) => {
              prevData.wishlists = userProfileLists
            }))
          }
        } catch (error) {
          console.error("Error editing list:", error)
        }
      }
    }),
    deleteWishList: builder.mutation<any, number>({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled
          const state = getState() as RootState
          const userProfile = state.user.profile
          const wishlistsWithFirstFourItems = state.user.wishListsWithFirstFourItems || []
          if (!userProfile) return
          const { userProfileLists, userWishLists } = getUpdatedStates(
            DELETE_LIST,
            wishlistsWithFirstFourItems,
            { id },
            null
          )

          dispatch(updateUserProfile({ ...userProfile, wishlistCount: userProfile?.wishlistCount - 1 }))
          dispatch(updateUserWishListsWithFirstFourItems({ wishlists: userProfileLists }))
          dispatch(updateUserWishLists({ wishlists: userWishLists }))

          dispatch(userProfileApi.util.updateQueryData("getUserProfile", undefined, (prevData) => {
            prevData.wishlistCount -= 1
          }))
          if (state.user.profile?.handle) {
            dispatch(userProfileApi.util.updateQueryData("getWishListsWithFirstFourItems", state.user.profile?.handle, (prevData) => {
              prevData.wishlists = userProfileLists
            }))
          }
          dispatch(wishListApi.util.updateQueryData("getWishLists", undefined, (prevData) => {
            if (!prevData.wishlists) return
            prevData.wishlists = prevData.wishlists.filter(list => Number(list.id) !== Number(id))
          }))
        } catch (error) {
          console.error("Error deleting list:", error)
        }
      }
    })
  })
})

export const {
  useLazyGetWishListQuery,
  useCreateWishListMutation,
  useEditWishListMutation,
  useDeleteWishListMutation,
  useLazyGetWishListsQuery
} = wishListApi
